import React, {useState} from "react";
import InertiaTable from "laravel-inertia-react-table";
import {Link, router, usePage} from "@inertiajs/react";
import {Alert, Badge, ButtonGroup, Container, Dropdown, Form} from "react-bootstrap";
import axios from "axios";
import Icon from "@mdi/react";
import {mdiSend} from "@mdi/js";
import {FlexToast, useExternalControls} from "laravel-inertia-backend";
import NoNewsletterBanner from "../../Components/Campaigns/NoNewsletterBanner.jsx";
import RecipientImportModal from "../../Components/Campaigns/RecipientImportModal.jsx";
import RecipientStatusBadgeTableCell from "../../Components/Campaigns/RecipientStatusBadgeTableCell.jsx";
import FeedbackCheckboxTableCell from "../../Components/Campaigns/FeedbackCheckboxTableCell.jsx";
import TimestampTableCell from "../../Components/TimestampTableCell.jsx";
import SendControlPanel from "../../Components/Campaigns/SendControlPanel.jsx";
import {campaignStatusLabel, campaignStatusVariant} from "../../Components/Campaigns/campaignStatusLabels.js";
import {formatGermanDateTime} from "../../Components/Campaigns/Wizard/scheduleForecast.js";

// Reihenfolge + Beschriftung der Status-Schnellauswahl. "null" = kein Filter
// (alle Empfänger).
const STATUS_FILTERS = [
    {value: null, label: "Alle"},
    {value: "pending", label: "Offen"},
    {value: "queued", label: "Eingeplant"},
    {value: "sent", label: "Gesendet"},
    {value: "failed", label: "Fehlgeschlagen"},
    {value: "skipped", label: "Übersprungen"},
];

/**
 * Haupt-Ansicht einer einzelnen Kampagne: Status + Fortschritt + Versand-
 * Steuerung ganz oben, darunter die vollständige Empfänger-Tabelle mit
 * Status-Schnellfilter. Ersetzt die vorherige, kaum auffindbare
 * "Empfänger verwalten"-Unterseite - das hier ist jetzt der zentrale Ort,
 * an dem sichtbar ist, an wen die Mail schon raus ist.
 */
export default function Recipients() {
    const {recipients, col_definitions, campaign, recipientStatusCounts, filters, schedulePlanSummary, tableColumnPreferences} = usePage().props;
    const {addToast} = useExternalControls();
    const [showImport, setShowImport] = useState(false);

    const activeStatusFilter = filters?.enums?.status ?? null;
    const totalRecipients = Object.values(recipientStatusCounts ?? {}).reduce((sum, n) => sum + Number(n), 0);

    // Ein-/ausblendbare Spalten der Empfänger-Tabelle (Auswahl "Spalten"
    // unten) - "Aktionen" (eigene Tabellen-Spalte, kein colDefinitions-
    // Eintrag) und "E-Mail" sind bewusst nicht Teil der Auswahl, damit sich
    // niemand die Tabelle unbrauchbar macht. Persistenz je Nutzer läuft
    // ohne Seitenneuladen über POST /campaigns/table-preferences.
    const toggleableColumns = tableColumnPreferences?.toggleable ?? [];
    const tableKey = tableColumnPreferences?.table_key ?? "campaign_recipients";
    const [hiddenColumns, setHiddenColumns] = useState(tableColumnPreferences?.hidden ?? []);

    const toggleColumnVisibility = async (columnId) => {
        const previousHidden = hiddenColumns;
        const nextHidden = hiddenColumns.includes(columnId)
            ? hiddenColumns.filter((id) => id !== columnId)
            : [...hiddenColumns, columnId];
        setHiddenColumns(nextHidden);

        try {
            await axios.post("/campaigns/table-preferences", {
                table_key: tableKey,
                columns: toggleableColumns.filter((id) => !nextHidden.includes(id)),
            });
        } catch (e) {
            setHiddenColumns(previousHidden);
            const message = e.response?.data?.message || e.message || "Spalteneinstellung konnte nicht gespeichert werden";
            addToast(<FlexToast variant="danger" title="Spalten" autohide={false} message={message}/>);
        }
    };

    const columnTitle = (columnId) => col_definitions?.find((col) => col.id === columnId)?.title ?? columnId;

    const transferToWeclapp = async (row) => {
        try {
            const {data} = await axios.post(`/campaigns/recipients/${row.id}/weclapp`);
            addToast(<FlexToast title="Weclapp" autohide={true} message={data.message}/>);
            router.reload({only: ["recipients"]});
        } catch (e) {
            const message = e.response?.data?.message || e.message || "Übertragung fehlgeschlagen";
            addToast(<FlexToast variant="danger" title="Weclapp" autohide={false} message={message}/>);
        }
    };

    const applyStatusFilter = (status) => {
        const remainingEnums = {...(filters?.enums ?? {})};
        if (status) {
            remainingEnums.status = status;
        } else {
            delete remainingEnums.status;
        }
        router.reload({
            data: {filter: {...filters, enums: remainingEnums}, page: 1},
            only: ["recipients", "filters"],
        });
    };

    return (
        <Container fluid className="p-4">
            <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                    <div className="d-flex align-items-center gap-2 mb-1">
                        <h1 className="mb-0">{campaign?.name ?? "Kampagne"}</h1>
                        <Badge bg={campaignStatusVariant(campaign?.status)}>{campaignStatusLabel(campaign?.status)}</Badge>
                    </div>
                    <div className="text-muted">
                        {totalRecipients} Empfänger insgesamt.
                    </div>
                </div>
                <div className="d-flex gap-2">
                    <Link href="/campaigns" className="btn btn-outline-secondary">
                        Zur Übersicht
                    </Link>
                    <Link href={`/campaigns/${campaign?.id}/edit`} className="btn btn-outline-secondary">
                        Kampagne bearbeiten
                    </Link>
                    <button type="button" className="btn btn-primary" onClick={() => setShowImport(true)}>
                        Aus Excel importieren
                    </button>
                </div>
            </div>
            <NoNewsletterBanner/>
            <SendControlPanel campaign={campaign}/>

            {schedulePlanSummary && (schedulePlanSummary.geplant > 0 || schedulePlanSummary.nicht_planbar > 0) && (() => {
                const dayCount = Object.keys(schedulePlanSummary.pro_tag ?? {}).length;
                const perDay = dayCount > 0 ? Math.round(schedulePlanSummary.geplant / dayCount) : schedulePlanSummary.geplant;
                return (
                    <Alert variant={schedulePlanSummary.nicht_planbar > 0 ? "warning" : "info"} className="mb-3">
                        {schedulePlanSummary.geplant > 0 && (
                            <div>
                                <strong>Versandplan:</strong> {schedulePlanSummary.geplant} Mail(s)
                                {dayCount > 0 && <> über {dayCount} Tag(e), etwa {perDay} pro Tag</>}
                                {schedulePlanSummary.erster && schedulePlanSummary.letzter && (
                                    <> - erste Mail {formatGermanDateTime(schedulePlanSummary.erster)}, letzte {formatGermanDateTime(schedulePlanSummary.letzter)}.</>
                                )}
                            </div>
                        )}
                        {schedulePlanSummary.nicht_planbar > 0 && (
                            <div className={schedulePlanSummary.geplant > 0 ? "mt-1" : ""}>
                                {schedulePlanSummary.nicht_planbar} Empfänger sind (noch) nicht planbar - bitte Zeitraum/Wochentage prüfen.
                            </div>
                        )}
                    </Alert>
                );
            })()}

            <div className="mb-3">
                <div className="text-muted small mb-1">Empfänger nach Status filtern:</div>
                <ButtonGroup>
                    {STATUS_FILTERS.map((entry) => {
                        const count = entry.value
                            ? Number(recipientStatusCounts?.[entry.value] ?? 0)
                            : totalRecipients;
                        const active = activeStatusFilter === entry.value;
                        return (
                            <button
                                key={entry.label}
                                type="button"
                                className={`btn btn-sm ${active ? "btn-primary" : "btn-outline-primary"}`}
                                onClick={() => applyStatusFilter(entry.value)}
                            >
                                {entry.label} ({count})
                            </button>
                        );
                    })}
                </ButtonGroup>
            </div>

            <div className="d-flex justify-content-end mb-2">
                <Dropdown autoClose="outside">
                    <Dropdown.Toggle variant="outline-secondary" size="sm" id="dropdown-recipient-columns">
                        Spalten
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="p-2">
                        <Form.Check
                            type="checkbox"
                            id="col-toggle-email"
                            label={columnTitle("email")}
                            checked
                            disabled
                            className="mb-1"
                        />
                        <Form.Check
                            type="checkbox"
                            id="col-toggle-actions"
                            label="Aktionen"
                            checked
                            disabled
                            className="mb-1"
                        />
                        <Dropdown.Divider/>
                        {toggleableColumns.map((columnId) => (
                            <Form.Check
                                key={columnId}
                                type="checkbox"
                                id={`col-toggle-${columnId}`}
                                label={columnTitle(columnId)}
                                checked={!hiddenColumns.includes(columnId)}
                                onChange={() => toggleColumnVisibility(columnId)}
                                className="mb-1"
                            />
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            <InertiaTable
                key="campaign-recipients-table"
                data={recipients}
                title=""
                actionsDisplay="column"
                hiddenCols={hiddenColumns}
                actions={(row) => row.weclapp_party_id ? [] : [
                    {icon: <Icon path={mdiSend} size={0.9}/>, alt: "Nach Weclapp übertragen", callback: (r) => transferToWeclapp(r)},
                ]}
                colDefinitions={[...col_definitions]}
                customCellRendererMap={{
                    status: RecipientStatusBadgeTableCell,
                    feedback_received: FeedbackCheckboxTableCell,
                    scheduled_for: TimestampTableCell,
                    sent_at: TimestampTableCell,
                    weclapp_transferred_at: TimestampTableCell,
                }}
            />

            <RecipientImportModal
                show={showImport}
                campaignId={campaign?.id}
                onClose={() => {
                    setShowImport(false);
                    router.reload({only: ["recipients", "recipientStatusCounts"]});
                }}
            />
        </Container>
    );
}
